import React, { useMemo } from "react";
import { useTheme } from "@mui/material";
import { Node } from "@iventis/domain-model/model/node";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Body2, inlineTextIconMargin, skeletonShimmer, styled } from "@iventis/styles";
import { getIndentPx, createBreadcrumb } from "@iventis/tree-browser";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { Theme } from "@emotion/react";
import { libraryListIcons } from "../plan-library/library-nodes";
import { getLibraryIconParameters } from "../plan-library/mapping-library.helpers";

interface TreeVisualizerProps {
    /** The node of whose parents we are trying to find */
    node: Node | undefined;
    /** The tree that the node resides in, you can get this by using the getTree function. */
    tree: Node | undefined;
    /** The max number of parent nodes we want to show. Defaults to 4 */
    maxNodes?: number;
}

/**
 * A visualiser to display parents of a node and its node. Displays a loading spinner if the node or tree is undefined
 * @returns A div containing a list of each element
 */
export const TreeVisualizer: React.FunctionComponent<TreeVisualizerProps> = ({ node, tree, maxNodes = 4 }) => {
    // Get the folder path to this node
    const pathToNode = useMemo(() => createBreadcrumb(node?.sourceId, tree), [node, tree]);

    const visualiserNodes = useMemo(() => {
        // Show loading spinner if we have no nodes yet
        if (pathToNode == null || pathToNode.length === 0) {
            return (
                <div>
                    <VisualizerNodeSkeleton indentLevel={0} data-testid="loading-skeleton">
                        Placeholder Node
                    </VisualizerNodeSkeleton>
                    <VisualizerNodeSkeleton indentLevel={1} style={{ opacity: "0.5" }}>
                        Placeholder Node
                    </VisualizerNodeSkeleton>
                </div>
            );
        }
        // Calcaulte if we have excess nodes
        const excessNodes = pathToNode.length - maxNodes;

        return (
            <div>
                {/* We want to first and last couple nodes if we have excess nodes, otherwise we can use the whole array */}
                {(excessNodes > 0 ? [pathToNode[0], ...pathToNode.slice(-maxNodes + 1)] : pathToNode).map((node, index) => {
                    // if we are displaying our second node and we have excess nodes, show the overload
                    if (index === 1 && excessNodes > 0) {
                        return <FillerNode indentLevel={index} nodeCount={excessNodes + 1} key={`node-${node.id}`} />;
                    }
                    return <VisualizerNode indentLevel={index} node={node} key={`node-${node.id}`} />;
                })}
            </div>
        );
    }, [pathToNode]);

    return visualiserNodes;
};

const VisualizerNode: React.FunctionComponent<{ node: Node; indentLevel: number }> = ({ node, indentLevel }) => {
    const theme = useTheme<Theme>();

    return (
        <VisualizerNodeContainer indentLevel={indentLevel} data-testid={`visualizer-node-${node.id}`}>
            <Body2>
                {libraryListIcons(theme, true)[node.type] ?? <FontAwesomeIcon icon={getLibraryIconParameters(node)} style={{ color: theme.shades.two }} /> ??
                    libraryListIcons(theme, true)[NodeType.Folder]}
            </Body2>
            <Body2 style={{ marginLeft: inlineTextIconMargin }}>{node.name}</Body2>
        </VisualizerNodeContainer>
    );
};

const FillerNode: React.FunctionComponent<{ nodeCount: number; indentLevel: number }> = ({ nodeCount, indentLevel }) => {
    const translate = useIventisTranslate();
    return (
        <VisualizerNodeContainer indentLevel={indentLevel} data-testid={`filler-node-${indentLevel}-${nodeCount}`}>
            <Body2 style={{ marginLeft: inlineTextIconMargin }}>{translate(Content.map4.layer_information.visualiser_overflow, { nodeCount })}</Body2>
        </VisualizerNodeContainer>
    );
};

const VisualizerNodeContainer = styled.div<{ indentLevel: number }>`
    display: flex;
    flex-direction: row;
    align-content: center;
    margin-left: ${({ indentLevel }) => getIndentPx(indentLevel)}px;
    padding: 2px;
`;

const VisualizerNodeSkeleton = styled(VisualizerNodeContainer)`
    ${skeletonShimmer({ sequence: 0 })}
    transform: scale(0.9);
`;
